import React, { useEffect, useState } from "react";
import { useDashboard } from "../context/DashboardContext";
import api from "../lib/api";
import { PageHeader, LiveBadge, DemoBadge } from "../components/common/PageParts";
import { Wifi, WifiOff, Save, Plug, KeyRound } from "lucide-react";
import { toast } from "sonner";

const KEY_PROVIDERS = [
  { key: "CRYPTOPANIC_API_KEY", label: "CryptoPanic (News)", desc: "Live crypto news headlines & sentiment" },
  { key: "TRADINGECONOMICS_API_KEY", label: "TradingEconomics (Macro/Fed)", desc: "Economic calendar, CPI, rate decisions" },
  { key: "GLASSNODE_API_KEY", label: "Glassnode (On-chain)", desc: "On-chain metrics & network flows" },
  { key: "SOSOVALUE_API_KEY", label: "SoSoValue (ETF Flow)", desc: "Spot BTC/ETH ETF net flows" },
];

export default function Settings() {
  const { providerStatus, settings, setSettings } = useDashboard();
  const [primary, setPrimary] = useState("kucoin");
  const [keys, setKeys] = useState({});

  useEffect(() => { if (settings) { setPrimary(settings.primaryProvider || "kucoin"); setKeys(settings.apiKeys || {}); } }, [settings]);

  const save = async () => {
    const s = await api.updateSettings({ primaryProvider: primary, apiKeys: keys });
    setSettings(s);
    toast.success("Settings saved");
  };
  const test = (kp) => {
    if (keys[kp.key]) toast.success(`${kp.label}: key present — connector ready`);
    else toast.error(`${kp.label}: no key set — section stays DEMO`);
  };

  return (
    <div className="ym-fade">
      <PageHeader title="Settings" subtitle="Data providers & external API connections" right={<button onClick={save} className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-semibold ym-hover" style={{ background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff" }}><Save size={15} /> Save</button>} />

      {/* Market data providers */}
      <div className="ym-panel p-4 mb-4">
        <div className="flex items-center gap-2 mb-3"><Plug size={16} style={{ color: "var(--accent-soft)" }} /><h3 className="text-[13px] font-bold">Market Data Providers</h3><LiveBadge /></div>
        <p className="text-[12px] mb-3" style={{ color: "var(--text-muted)" }}>Public providers (no key required). Choose the primary; the other is automatic fallback. Pairs are never silently swapped — the active provider is always shown.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {providerStatus.map((p) => (
            <div key={p.id} onClick={() => setPrimary(p.id)} className="rounded-xl p-3.5 cursor-pointer ym-hover"
              style={{ background: "var(--panel-2)", border: `1px solid ${primary === p.id ? "rgba(139,92,246,.5)" : "var(--border)"}` }}>
              <div className="flex items-center gap-2">
                {p.online ? <Wifi size={15} style={{ color: "var(--green)" }} /> : <WifiOff size={15} style={{ color: "var(--red)" }} />}
                <span className="font-bold text-[13px]">{p.name}</span>
                {primary === p.id && <span className="pill" style={{ color: "var(--accent-soft)", background: "rgba(139,92,246,.14)", borderColor: "transparent" }}>Primary</span>}
                <span className="ml-auto text-[11px] tabnum" style={{ color: p.online ? "var(--green)" : "var(--red)" }}>{p.online ? `${p.latencyMs}ms` : "offline"}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API key connectors */}
      <div className="ym-panel p-4">
        <div className="flex items-center gap-2 mb-3"><KeyRound size={16} style={{ color: "var(--accent-soft)" }} /><h3 className="text-[13px] font-bold">Premium Connectors</h3><DemoBadge /></div>
        <p className="text-[12px] mb-3" style={{ color: "var(--text-muted)" }}>These power Market Intelligence, Macro, On-chain & ETF flow. Add a key to switch a section from DEMO to LIVE. Keys are stored server-side in your settings.</p>
        <div className="space-y-2.5">
          {KEY_PROVIDERS.map((kp) => (
            <div key={kp.key} className="rounded-xl p-3" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-[12px]">{kp.label}</span>
                {keys[kp.key] ? <LiveBadge /> : <DemoBadge />}
                <span className="ml-auto text-[10px]" style={{ color: "var(--text-dim)" }}>{kp.desc}</span>
              </div>
              <div className="flex gap-2">
                <input type="password" placeholder={`Paste ${kp.key}`} value={keys[kp.key] || ""} onChange={(e) => setKeys({ ...keys, [kp.key]: e.target.value })}
                  className="flex-1 rounded-lg px-3 py-2 text-[12px] outline-none" style={{ background: "var(--panel)", border: "1px solid var(--border)", color: "var(--text)" }} />
                <button onClick={() => test(kp)} className="rounded-lg px-4 text-[12px] font-semibold ym-hover" style={{ background: "var(--panel)", border: "1px solid var(--border)", color: "var(--accent-soft)" }}>Test Connection</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
